import React, { useState, useEffect } from "react";
import { SpecialZoomLevel, Viewer, Worker } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import * as pdfjsLib from "pdfjs-dist";

import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";

const pdfjsVersion = pdfjsLib.version;

const CustomLoader = () => {
  return (
    <div className="d-flex justify-content-center align-items-center px-5 py-5">
      <div className="spinner-border" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );
};

const PDFViewer = ({ url }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfSource, setPdfSource] = useState(null);
  const [key, setKey] = useState(0);

  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    sidebarTabs: (defaultTabs) => [defaultTabs[0]],
    toolbarPlugin: {
      fullScreenPlugin: {
        enableShortcuts: false,
      },
      propertiesPlugin: {
        enableShortcuts: false,
      },
      rotatePlugin: {
        enableShortcuts: false,
      },
      scrollModePlugin: {
        enableShortcuts: false,
      },
      selectionModePlugin: {
        enableShortcuts: false,
      },
      themePlugin: {
        enableShortcuts: false,
      },
      zoomPlugin: {
        enableShortcuts: false,
      },
    },
  });

  useEffect(() => {
    if (!url) {
      setPdfSource(null);
      setLoading(true);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setPdfSource(url);
    setKey((prev) => prev + 1);
  }, [url]);

  const handleDocumentLoad = (e) => {
    console.log("PDF loaded:", e.doc.numPages, "pages");
    setLoading(false);
  };

  const handleError = (error) => {
    console.error("PDF loading error:", error);
    setError(error);
    setLoading(false);
  };

  if (!url) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "400px",
          backgroundColor: "#f5f5f5",
          border: "1px solid #dee2e6",
          borderRadius: "6px",
        }}
      >
        <p style={{ color: "#666", margin: 0 }}>No PDF URL provided</p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "400px",
          backgroundColor: "#f8f9fa",
          border: "1px solid #dee2e6",
          borderRadius: "6px",
          flexDirection: "column",
          gap: "10px",
        }}
      >
        <div style={{ color: "#dc3545", fontSize: "48px" }}>⚠️</div>
        <div style={{ color: "#dc3545", textAlign: "center" }}>
          <div style={{ fontSize: "18px", marginBottom: "5px" }}>
            Failed to load PDF document
          </div>
          <small style={{ fontSize: "14px" }}>{error.message}</small>
        </div>
        <button
          onClick={() => {
            setError(null);
            setLoading(true);
            setKey((prev) => prev + 1);
          }}
          style={{
            padding: "8px 16px",
            backgroundColor: "#007bff",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        height: "90vh",
        width: "100%",
        border: "1px solid #dee2e6",
        borderRadius: "6px",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {loading && <CustomLoader />}

      {pdfSource && (
        <Worker
          workerUrl={`https://unpkg.com/pdfjs-dist@${pdfjsVersion}/build/pdf.worker.min.js`}
        >
          <div style={{ height: "100%" }}>
            <Viewer
              key={key}
              fileUrl={pdfSource}
              plugins={[defaultLayoutPluginInstance]}
              defaultScale={SpecialZoomLevel.PageFit}
              onDocumentLoad={handleDocumentLoad}
              onPageChange={(e) => {
                console.log("Page changed to:", e.currentPage + 1);
              }}
              onZoom={(e) => {
                console.log("Zoom changed to:", e.scale);
              }}
              renderError={handleError}
            />
          </div>
        </Worker>
      )}

      <style jsx global>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        /* Hide print and download buttons */
        [data-testid="toolbar__print-button"],
        [data-testid="get-file__download-button"],
        [data-testid="open__button"],
        [data-testid="open__menu"],
        [data-testid="toolbar__download-button"],
        [data-testid="get-file__download-menu"],
        [data-testid="toolbar__download-menu"],
        [data-testid="toolbar__print-menu"],
        [data-testid="print__menu"],
        [aria-label="Download"],
        [aria-label="Print"],
        .rpv-toolbar__item:has([aria-label="Download"]),
        .rpv-toolbar__item:has([aria-label="Print"]) {
          display: none !important;
        }

        /* More specific selectors for menu items */
        [data-testid="core__menu-item-print"],
        [data-testid="core__menu-item-download"] {
          display: none !important;
        }

        /* Customize the viewer appearance */
        .rpv-core__viewer {
          background-color: #f5f5f5;
        }

        /* Customize toolbar */
        .rpv-toolbar {
          background-color: #f8f9fa;
          border-bottom: 1px solid #dee2e6;
        }

        /* Customize sidebar */
        .rpv-default-layout__sidebar {
          background-color: #ffffff;
          border-right: 1px solid #dee2e6;
        }
      `}</style>
    </div>
  );
};

export default PDFViewer;
